import React, {useRef, useState} from 'react';
import {Button, App, Space} from "antd";
import {UploadStatuses} from "../../../../../../models";
import useMortgageUploadService from "../../../../../providers/mortgage/useMortgageUploadService";
import useMortgage from "../../../../../providers/mortgage/useMortgage";
import useMortgageDocumentsService, {
    getUserDocumentKey
} from "../../../../../providers/mortgage/useMortgageDocumentsService";
import {CheckCircleOutlined, LoadingOutlined} from "@ant-design/icons";
import {green} from "@ant-design/colors";
import {capitaliseWords} from "../../../../../../assets/scripts/string-functions";
import {sortDateSorter} from "./standardised-upload/UploadsTable";
import dayjs from "dayjs";

export const getBundleFileName = (section, mortgage) => {
    let fileName = ''
    if (!!section.applicant) {
        fileName += mortgage[`applicant${section.applicant}`].firstName
        fileName += '-'
    }
    fileName += section.title.replace(/\s/g, '-')
    fileName += '.pdf'
    fileName = fileName.toLowerCase()

    return fileName
}
export const BUNDLE_FOLDER = 'bundled-uploads'

export const getBundleSectionFileName = (section) => {
    let fileName = section.personalTitle.toLowerCase().replace(/'/g, '').replace(/[^a-z0-9]/gi, '-');
    // if (section.data?.maskedAccountNumber) {
    //     fileName += '-' + section.data.maskedAccountNumber.replace(/[^a-z0-9]/gi, '');
    // }
    fileName += '.pdf'

    return fileName
}
export function UploadSectionBundler({section}) {
    const folder = BUNDLE_FOLDER
    const uploadService = useMortgageUploadService()
    const documentService = useMortgageDocumentsService()
    const mortgage = useMortgage()
    const app = App.useApp()
    const [state, setState] = useState({
        loading: false,
        success: false
    })
    let fileName = getBundleSectionFileName(section)
    const existingBundles = mortgage.UserMortgageDocuments.filter(it => it.folder === folder && it.stage === section.stage && it.fileName === fileName && it.submissionID === mortgage?.activeSubmission?.id)
    const existingBundle = existingBundles.length ? existingBundles.sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)))[0] : null

    const bundleCreatedAt = existingBundle ? dayjs(existingBundle.createdAt) : null


    let bundleExpired = false
    if (bundleCreatedAt) {
        // are there new active uploads in this section?
        const hasNewUploads = section.activeUploads.some(upload => {
            const uploadCreatedAt = dayjs(upload.props.upload.createdAt)
            return bundleCreatedAt.isBefore(uploadCreatedAt);
        })
        bundleExpired = hasNewUploads
    }



    const openSectionBundle = async () => {
        await documentService.open(existingBundle)
    }

    const bundleSectionFiles = async () => {
        if (!mortgage.applicant1.identityID) {
            app.notification.error({message: `You can't access the customers secure folder. Have them register/sign into the app`})
            return
        }
        setState(s => ({
            ...s,
            loading: true,
            success: false
        }))
        //set config for the merge function on the api

        //create the file name
        const doc = {
            fileName,
            mortgageID: mortgage.id,
            folder,
        }
        if (mortgage.activeSubmission) {
            doc.submissionID = mortgage.activeSubmission?.id
        }

        const path = getUserDocumentKey(doc)

        const config = {
            map: [],
            destination: `private/${mortgage.applicant1.identityID}/${path}`
        }
        //get the uploads from the section

        if (!section.activeUploads || section.activeUploads.length === 0) {
            app.notification.error({message: `No uploads found for this section. Please upload files before bundling.`})

            setState(s => ({
                ...s,
                loading: false
            }))
            return
        }
        const uploads = section.uploads.filter(it=>it.isActive).map(it => it.props.upload)

        //reorder them by the section arrangement
        config.map = uploads.reverse().map(it => {
            let {
                path,
                config: uploadConfig
            } = uploadService.get(it)
            return {
                id: it.id,
                key: `private/${uploadConfig.identityId}/${path}`,
                omit: it.omit || []
            }
        })
        let document = {
            title: section.personalTitle,
            identityID: mortgage.applicant1.identityID,
            folder,
            fileName,
            stage: section.stage,

        }
        if (mortgage.activeSubmission) {
            document.submissionID = mortgage.activeSubmission?.id
        }
        await documentService.createBundle(config, document)
        setState(s => ({
            ...s,
            loading: false,
            success: true
        }))
    }
    if (!section.activeUploads || section.activeUploads.length === 0) {
        return null
    }
    return (
            <Space.Compact>
            <Button onClick={bundleSectionFiles} danger={!existingBundle || bundleExpired}>
                {bundleExpired && <span>Re-</span>}Bundle
                {state.loading && <LoadingOutlined className="ms-3"/>}
                {!state.loading && !!existingBundle && !bundleExpired &&
                    <CheckCircleOutlined style={{color: green.primary}} className="ms-3"/>}
            </Button>
            {!state.loading && !!existingBundle && (<Button danger={bundleExpired} onClick={openSectionBundle}>File</Button>)}
            </Space.Compact>


    );
}

export function useBundleAllSections(stage){
    const folder = BUNDLE_FOLDER
    const uploadService = useMortgageUploadService()
    const documentService = useMortgageDocumentsService()
    const mortgage = useMortgage()
    const [state, setState] = useState({
        loading: false,
        success: false
    })
    const sections = mortgage.uploadSections.filter(it=>it.derivedUserStages.includes(stage) && it.activeUploads && it.activeUploads.length > 0 && it.isActive)
    const bundlingRef = useRef(null)
    const bundle = async ()=>{
        if (!mortgage.applicant1.identityID || bundlingRef.current) {
            return
        }
        bundlingRef.current = true
        setState(s => ({
            ...s,
            loading: true,
            success: false
        }))
        const creationsCollection = []
        const promises = sections.map(section=>{
                let fileName = getBundleSectionFileName(section)

                //create the file name
                const doc = {
                    fileName,
                    mortgageID: mortgage.id,
                    folder,
                }
                if (mortgage.activeSubmission) {
                    doc.submissionID = mortgage.activeSubmission?.id
                }
                const path = getUserDocumentKey(doc)
                const config = {
                    map: [],
                    destination: `private/${mortgage.applicant1.identityID}/${path}`
                }
                //get the uploads from the section

                const uploads = section.uploads.filter(it=>it.isActive).map(it => it.props.upload)

                //reorder them by the section arrangement
                config.map = uploads.reverse().map(it => {
                    let {
                        path,
                        config: uploadConfig
                    } = uploadService.get(it)
                    return {
                        id: it.id,
                        key: `private/${uploadConfig.identityId}/${path}`,
                        omit: it.omit || []
                    }
                })
                let document = {
                    title: section.personalTitle,
                    identityID: mortgage.applicant1.identityID,
                    folder,
                    fileName,
                    stage: section.stage,
                }
                if (mortgage.activeSubmission) {
                    document.submissionID = mortgage.activeSubmission?.id
                }
                return documentService.createBundle(config, document, creationsCollection)
            })
        await Promise.all(promises)
        await mortgage.mutate({create:creationsCollection})
        setState(s => ({
            ...s,
            loading: false,
            success: true
        }))
    }

    return {...state, bundle}
}

function Bundler(props) {
    const folder = BUNDLE_FOLDER
    const app = App.useApp()
    const uploadService = useMortgageUploadService()
    const documentService = useMortgageDocumentsService()
    const mortgage = useMortgage()

    const [state, setState] = useState({
        loading: false,
    })
    let fileName = getBundleFileName(props.section, mortgage)

    const existingBundle = mortgage.UserMortgageDocuments.find(it => it.folder === folder && it.stage === props.section.record.applicationStage && it.fileName === fileName && it.submissionID === mortgage?.activeSubmission?.id)
    const bundleFiles = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (!mortgage.applicant1.identityID) {
            app.notification.error({message: `You can't access the customers secure folder. Have them register/sign into the app`})
            return
        }
        setState(s => ({
            ...s,
            loading: true,
            success: false
        }))
        //set config for the merge function on the api

        //create the file name
        const doc = {
            fileName,
            mortgageID: mortgage.id,
            folder,
        }
        if (mortgage.activeSubmission) {
            doc.submissionID = mortgage.activeSubmission?.id
        }
        const path = getUserDocumentKey(doc)
        const config = {
            map: [],
            destination: `private/${mortgage.applicant1.identityID}/${path}`
        }
        //get the uploads from the section

        const uploads = props.section.verified.isVerifiable ? props.section.verified.rows.map(it => it.upload).sort(sortDateSorter) : props.section.uploads.filter(it => it.status !== UploadStatuses.REJECTED && it.status !== UploadStatuses.EXPIRED && !it.exclude)

        //reorder them by the section arrangement
        config.map = uploads.map(it => {
            let {
                path,
                config: uploadConfig
            } = uploadService.get(it)
            return {
                id: it.id,
                key: `private/${uploadConfig.identityId}/${path}`,
                omit: it.omit || []
            }
        })
        // config.map = props.section.record.arrangement.reverse().map(id => {
        //     let upl = uploads.find(it => it.id === id)
        //     if (upl) {
        //         let {path, config: uploadConfig} = uploadService.get(upl)
        //         return {
        //             id: upl.id,
        //             key: `private/${uploadConfig.identityId}/${path}`,
        //             omit: upl.omit || []
        //         }
        //     }
        //     return null
        // }).filter(it => !!it)
        let document = {
            title: mortgage.applicant1.firstName + " - " + capitaliseWords(props.section.title),
            identityID: mortgage.applicant1.identityID,
            folder,
            fileName,
            stage: props.section.record.applicationStage,

        }
        if (mortgage.activeSubmission) {
            document.submissionID = mortgage.activeSubmission?.id
        }
        await documentService.createBundle(config, document)
        setState(s => ({
            ...s,
            loading: false,
            success: true
        }))
    }

    const openFile = (e) => {
        e.preventDefault()
        e.stopPropagation()
        documentService.open(existingBundle)
    }
    return (<div className="d-row gap-15">
            <Button onClick={bundleFiles}>
                Bundle
                {state.loading && <LoadingOutlined className="ms-3"/>}
                {!state.loading && !!existingBundle &&
                    <CheckCircleOutlined style={{color: green.primary}} className="ms-3"/>}
            </Button>
            {!state.loading && !!existingBundle && (<Button onClick={openFile}>File</Button>)}
        </div>

    );
}

export default Bundler;